import React, { useEffect } from 'react';
import TopBar from 'components/TopBar/TopBar';
import Copyright from 'components/Copyright/Copyright';
import routes from 'routes/routes';
import {
	Wrapper,
	Intro,
	IntroEyebrow,
	IntroTitle,
	IntroLead,
	IntroMeta,
	P,
	List,
	Callout,
	TableWrap,
	Table,
	Th,
	Td,
	Address,
} from 'components/LegalDocument/styled';
import {
	Content,
	Prose,
	StepsPanel,
	StepsTitle,
	Steps,
	Step,
	SectionTitle,
} from './styled';

/**
 * Google Play's Data Safety form requires a standalone, directly-linkable
 * page for deletion requests — not a fragment of the Privacy Policy — that
 * names the app, prominently states the steps, and specifies what is
 * deleted versus retained. This page is that link; Section 7 of the Privacy
 * Policy remains the full legal statement of the same right and both must
 * stay consistent with each other if either changes.
 */
const DataDeletion = () => {
	useEffect(() => {
		document.title = 'Delete Your Data | Flass';
	}, []);

	return (
		<Wrapper id="top">
			<TopBar />

			<Intro>
				<IntroEyebrow>Flass</IntroEyebrow>
				<IntroTitle>Request account and data deletion</IntroTitle>
				<IntroLead>
					You can ask us to delete your Flass account and the
					personal information tied to it at any time. This page
					explains how, and exactly what is removed.
				</IntroLead>
				<IntroMeta>
					<span>Effective August 11, 2026</span>
				</IntroMeta>
			</Intro>

			<Content>
				<Prose>
					<StepsPanel>
						<StepsTitle>How to request deletion</StepsTitle>
						<Steps>
							<Step>
								Email <strong>ergilaze@gmail.com</strong> from
								the address your Flass account uses, with the
								subject{' '}
								<strong>
									&ldquo;Data deletion request&rdquo;
								</strong>
								.
							</Step>
							<Step>
								Include your <strong>full name</strong> and
								your <strong>institution</strong>, so we can
								locate the right account.
							</Step>
							<Step>
								We confirm your identity, then delete your
								account and the personal information
								described below, usually within{' '}
								<strong>30 days</strong>.
							</Step>
						</Steps>
					</StepsPanel>

					<P>
						If your account was set up for you by a university or
						other institution, we may need to direct your request
						to that institution&rsquo;s administrator, consistent
						with our{' '}
						<a href={routes.PRIVACY} style={{ color: '#4DAC74' }}>
							Privacy Policy
						</a>
						.
					</P>

					<SectionTitle>What gets deleted</SectionTitle>
					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Data</Th>
									<Th>Outcome</Th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<Td>Profile — name, email, password</Td>
									<Td>Deleted</Td>
								</tr>
								<tr>
									<Td>Device / push notification tokens</Td>
									<Td>Deleted</Td>
								</tr>
								<tr>
									<Td>
										Saved and pinned message references
									</Td>
									<Td>Deleted</Td>
								</tr>
								<tr>
									<Td>
										Marketplace listings and job posts you
										created
									</Td>
									<Td>Deleted</Td>
								</tr>
								<tr>
									<Td>
										Poll and survey responses you gave
									</Td>
									<Td>Deleted</Td>
								</tr>
								<tr>
									<Td>
										Messages and files already shared into
										a room or group
									</Td>
									<Td>Kept in that room&rsquo;s history</Td>
								</tr>
							</tbody>
						</Table>
					</TableWrap>

					<P>
						We do not retroactively remove content from a
						conversation other people are relying on. A message
						you sent stays visible to the room it was sent in,
						attributed as it appeared at the time, even after
						your account is gone — the same way it would if you
						left that room.
					</P>

					<SectionTitle>Retention beyond deletion</SectionTitle>
					<Callout>
						We may keep limited information for longer where the
						law requires it, to resolve an active dispute, to
						enforce our agreements, or where your institution
						requires retention consistent with its own
						record-keeping obligations. This mirrors
						Section&nbsp;6 (Data Retention) of our{' '}
						<a href={routes.PRIVACY} style={{ color: '#4DAC74' }}>
							Privacy Policy
						</a>
						.
					</Callout>

					<SectionTitle>Questions</SectionTitle>
					<P>Contact us any time at:</P>
					<Address>
						Flass
						<br />
						ergilaze@gmail.com
					</Address>

					<List style={{ marginTop: 30 }}>
						<li>
							Read the full{' '}
							<a
								href={routes.PRIVACY}
								style={{ color: '#4DAC74' }}
							>
								Privacy Policy
							</a>
						</li>
						<li>
							Read the{' '}
							<a
								href={routes.TERMS}
								style={{ color: '#4DAC74' }}
							>
								Terms of Service
							</a>
						</li>
					</List>
				</Prose>
			</Content>

			<Copyright />
		</Wrapper>
	);
};

export default DataDeletion;
